// =========================================================================
// Minimal protobuf wire-format READER.
//
// Deliberately read-only. We never need to encode protobuf — the node builds
// transactions — but we absolutely need to decode them ourselves, because the
// Ledger shows only a hash when signing a permission update and the operator
// otherwise has no way to know what they are approving.
//
// Wire format (all we need):
//   tag = (field_number << 3) | wire_type
//   0 = varint, 1 = 64-bit, 2 = length-delimited, 5 = 32-bit
//
// It reads nothing that protobuf-java could read differently. java-tron parses
// these bytes with protobuf-java, and an input on which two parsers disagree is
// a way to show the operator one transaction while the chain runs another. So
// these are refused, not interpreted:
//   - a tag wider than 32 bits. protobuf-java keeps a tag's low 32 bits, so
//     `a0 80 80 80 10` is field 4 to it and field 536,870,916 to a reader that
//     keeps them all — a hidden `threshold` (review finding B1).
//   - a varint wider than 64 bits. protobuf-java drops the bits past 64.
//   - a varint written longer than it needs to be. Both would read the same
//     value; nothing that legitimately builds these bytes writes one, and a
//     second spelling of a value is one more thing for two readers to differ on.
// =========================================================================

export const WIRE = { VARINT: 0, I64: 1, LEN: 2, I32: 5 };

const MAX_TAG = 0xffffffffn;

function readVarint(buf, pos) {
  const start = pos;
  let result = 0n;
  let shift = 0n;
  for (;;) {
    if (pos >= buf.length) throw new Error('varint ran past end of buffer');
    const byte = buf[pos++];
    // The tenth byte lands at bit 63, where only its lowest bit fits.
    if (shift === 63n && byte > 1) throw new Error('varint exceeds 64 bits');
    result |= BigInt(byte & 0x7f) << shift;
    if ((byte & 0x80) === 0) {
      if (byte === 0 && pos - start > 1) throw new Error('varint is written longer than it needs to be');
      break;
    }
    shift += 7n;
  }
  return [result, pos];
}

/**
 * Every field of a message, in the order written, as { field, wire, value }.
 * value is a bigint for a varint and a Uint8Array for everything else.
 */
export function parseFields(buf) {
  const out = [];
  let pos = 0;

  while (pos < buf.length) {
    const [tag, p1] = readVarint(buf, pos);
    pos = p1;
    if (tag > MAX_TAG) {
      throw new Error(
        `tag ${tag} is wider than 32 bits — protobuf-java would read it as field ${(tag & MAX_TAG) >> 3n}`,
      );
    }
    const field = Number(tag >> 3n);
    const wire = Number(tag & 7n);
    if (field === 0) throw new Error('invalid field number 0');

    if (wire === WIRE.VARINT) {
      const [v, p2] = readVarint(buf, pos);
      pos = p2;
      out.push({ field, wire, value: v });
    } else if (wire === WIRE.LEN) {
      const [lenBig, p2] = readVarint(buf, pos);
      const len = Number(lenBig);
      if (p2 + len > buf.length) throw new Error(`length-delimited field ${field} overruns buffer`);
      out.push({ field, wire, value: buf.slice(p2, p2 + len) });
      pos = p2 + len;
    } else if (wire === WIRE.I64) {
      if (pos + 8 > buf.length) throw new Error('fixed64 overruns buffer');
      out.push({ field, wire, value: buf.slice(pos, pos + 8) });
      pos += 8;
    } else if (wire === WIRE.I32) {
      if (pos + 4 > buf.length) throw new Error('fixed32 overruns buffer');
      out.push({ field, wire, value: buf.slice(pos, pos + 4) });
      pos += 4;
    } else {
      throw new Error(`unsupported wire type ${wire} on field ${field}`);
    }
  }
  return out;
}

/**
 * Parse a protobuf message into a Map of fieldNumber → array of values.
 * Values are bigint (varint) or Uint8Array (length-delimited and fixed).
 * Repeated fields naturally accumulate; unknown fields are kept, not dropped,
 * so a caller can detect that a message carried something unexpected.
 */
export function parseMessage(buf) {
  const fields = new Map();
  for (const { field, value } of parseFields(buf)) {
    if (!fields.has(field)) fields.set(field, []);
    fields.get(field).push(value);
  }
  return fields;
}

/**
 * Parse a message against its schema: `schema` maps each field number to
 * `{ name, wire, repeated }`, or to `{ name, refuse: 'why' }` for a field the
 * message has but the caller will not accept. Refused, besides those:
 *   - a field number the schema does not have. Every reader skips it, so it is
 *     shown to nobody — and a node hashes it all the same.
 *   - a known field with another wire type. protobuf-java files it under
 *     unknown fields and reads the field as absent.
 *   - a singular field written twice. protobuf-java keeps the last scalar and
 *     merges messages; there is no one value to show.
 * Returns the same Map as parseMessage, so the accessors below apply.
 */
export function parseStrict(buf, schema, what) {
  const fields = new Map();
  for (const { field, wire, value } of parseFields(buf)) {
    const spec = schema[field];
    if (!spec) throw new Error(`${what} carries field ${field}, which is not in its schema`);
    if (spec.refuse) throw new Error(`${what} carries ${spec.name} (field ${field}): ${spec.refuse}`);
    if (wire !== spec.wire) {
      throw new Error(`${what}.${spec.name} is written with wire type ${wire}, not ${spec.wire}`);
    }
    const seen = fields.get(field);
    if (seen && !spec.repeated) throw new Error(`${what}.${spec.name} appears more than once`);
    if (seen) seen.push(value);
    else fields.set(field, [value]);
  }
  return fields;
}

// Convenience accessors. Proto3 omits default values, so "absent" means zero /
// empty — the callers below encode that expectation explicitly.
export function one(fields, num) {
  const v = fields.get(num);
  if (!v) return undefined;
  if (v.length !== 1) throw new Error(`field ${num} appeared ${v.length} times, expected once`);
  return v[0];
}

export function many(fields, num) {
  return fields.get(num) ?? [];
}

export function num(fields, n, dflt = 0n) {
  const v = one(fields, n);
  if (v === undefined) return dflt;
  if (typeof v !== 'bigint') throw new Error(`field ${n} is not a varint`);
  return v;
}

export function bytes(fields, n) {
  const v = one(fields, n);
  if (v === undefined) return undefined;
  if (!(v instanceof Uint8Array)) throw new Error(`field ${n} is not length-delimited`);
  return v;
}

export function utf8(fields, n) {
  const b = bytes(fields, n);
  return b === undefined ? undefined : new TextDecoder().decode(b);
}

// ---- reading a value as protobuf-java reads it -----------------------------
//
// protobuf-java reads an int32 or an enum by keeping the low 32 bits of the
// varint, and an int64 as a signed 64-bit number. A value it would read as
// anything other than what we read is refused here, never shown. Each returns
// 0n when the field is absent, as proto3 means.

export const INT32_MAX = 2n ** 31n - 1n;
export const INT64_MAX = 2n ** 63n - 1n;

export function int32(fields, n, what) {
  const v = num(fields, n, 0n);
  if (v > INT32_MAX) throw new Error(`${what} is ${v}, outside an int32 — the chain would read it as another number`);
  return v;
}

export function int64(fields, n, what) {
  const v = num(fields, n, 0n);
  if (v > INT64_MAX) throw new Error(`${what} is ${v}, outside an int64 — the chain would read it as negative`);
  return v;
}

/** A proto3 string: '' when absent; refused if it is not UTF-8, which protobuf-java refuses too. */
export function text(fields, n, what) {
  const b = bytes(fields, n);
  if (b === undefined) return '';
  try {
    return new TextDecoder('utf-8', { fatal: true, ignoreBOM: true }).decode(b);
  } catch {
    throw new Error(`${what} is not valid UTF-8 — the chain refuses it, and it cannot be shown as written`);
  }
}
